/**
 * Maps member-360's member data into the CreateClaimNativeProps context shapes
 * (member, memberId). Mirrors the approach in toPatientObject.ts for the
 * @astrana/ui MemberProfile patient descriptor.
 *
 * IMPORTANT: CreateClaimNativeProps.member / .provider / .dateOfService are
 * typed `unknown` in P1 (placeholders — see CreateClaim.types.ts header). The
 * module internally casts them via its own `asMemberSeed` / `asProviderSeed`
 * helper interfaces (CreateClaim.tsx):
 *
 *   interface HostMemberSeed {
 *     RmiMembKeyId?: string;   // PP's RmiMembKeyId (mkey)
 *     RmiHpCode?: string;      // health plan code
 *     RmiStartDate?: string;   // coverage-span start date
 *     RmiEndDate?: string;     // coverage-span end date
 *     RmiSex?: string;         // sex
 *   }
 *
 * ONE ROW, OR NOTHING. `RmiMembKeyId`, `RmiHpCode` and `RmiStartDate` are not
 * three independent facts about a member — they are three columns of ONE
 * `vRmiMemberSearch` row, and PP-API's claim shell endpoint uses them as a
 * composite key. `spRmiCreateTempClaim` selects the member with
 *
 *     RmiMembKeyId = @MembKeyId
 *     AND RmiCompanyId = @CompanyId
 *     AND convert(varchar, RmiStartDate, 112) = convert(varchar, @StartDate, 112)
 *     AND RmiHpCode = @HpCode
 *
 * and then INSERTs the temp claim whether or not that matched: a miss leaves
 * every `@MEMB*` value NULL and still answers HTTP 200, i.e. a silent claim shell
 * with no patient on it. `RmiStartDate` is itself COMPUTED per coverage span
 * (`CASE WHEN RmiOpFromDt <= RmiPcpFromDt THEN RmiPcpFromDt ELSE RmiOpFromDt
 * END`, one row per health-plan span × PCP span), which is why NO other system's
 * date is a substitute for it — not Mongo's `enrollmentDate`, not connect-gql's
 * `insurance.effectiveDate`; both are arithmetically different values. Nor can
 * the three be assembled from different sources: only a real row's own trio
 * matches.
 *
 * So this file no longer maps a member "object" to a claim seed field by field.
 * It converts ONE resolved PP row (`members/resolvePpMembId.ts`'s
 * `resolvePpMemberRecord`, or the search row a PP-path profile already carries in
 * `location.state`) into the module's seed, keeping the values together from the
 * row to the request. When no such row can be resolved, the Create Claim entry
 * gate blocks — see claims/CreateClaimGate.tsx.
 *
 * ⚠️ RmiHpCode IS THE HEALTH PLAN CODE, NOT THE IPA/TENANT CODE. An earlier
 * revision of this file mapped `RmiHpCode ← ipaCode` (e.g. "AAMG"), which is a
 * different concept entirely: `ipaCode`/`companyId` is the tenant (the
 * X-Backend-Id / DB-connection selector, passed separately as the module's
 * `companyId` prop), while `hpCode` is the member's health plan (e.g. "HNCC",
 * "BSPP", "CRCAMC"). The module forwards this value verbatim as the REQUIRED
 * `hpCode` query param of `GET ClaimMember/CreateClaim`, and the Create Claim
 * entry gate's block rules (claims/claimMemberAccess.ts) compare it against
 * health-plan codes — so an IPA code there is silently wrong in both places.
 *
 * WHITESPACE IS ABSENCE. PP/EZCap serve these out of CHAR columns, so a padded
 * `"   "` is a real possibility and is NOT a usable value: PP-API's `[Required]`
 * check rejects a blank/padded `startDate` or `hpCode` exactly like a missing one
 * (HTTP 400), and an untrimmed truthy hpCode would satisfy the gate's "is the
 * plan known?" test while matching none of its payer-routing block rules,
 * silently disabling them for that member. `ppClaimSeedFromRecord` therefore
 * trims all three and refuses the row unless all three survive.
 *
 * TODO(create-claim P2): when CreateClaimNativeProps.member gains a concrete
 * type, remove the `unknown` cast below and type against that interface directly.
 */

import type { CreateClaimMemberContext } from "@astrana/ui/modules";

import type { MemberByIdResult } from "./graphql/memberById";

/**
 * The subset of a Provider Portal member row a claim seed reads. Structural on
 * purpose so it is satisfied by BOTH `ProviderPortalMember` (a freshly resolved
 * row) and MemberProfilePage's `PpMemberState` (the search row a PP-path profile
 * carries in `location.state`) — the two are the same row from the same view.
 */
export interface PpClaimRecordLike {
  membKeyId?: string | null;
  healthPlanCode?: string | null;
  startDate?: string | null;
  gender?: string | null;
}

/**
 * The three-plus-one values a claim seed needs, proven to have come from ONE PP
 * member row. Produced only by `ppClaimSeedFromRecord`, so there is no way to
 * assemble one from separately-sourced fields.
 */
export interface PpClaimMemberSeed {
  /** PP's `RmiMembKeyId`. */
  membKeyId: string;
  /** PP's `RmiHpCode` — the HEALTH PLAN, not the IPA. */
  hpCode: string;
  /** PP's `RmiStartDate` — the row's computed coverage-span start. */
  startDate: string;
  /** PP's `RmiSex`, when the row carries it. Not required by PP-API. */
  sex?: string;
}

/**
 * Extract the claim seed from ONE PP member row, or null when that row cannot
 * seed a claim.
 *
 * Returns null unless all three required values survive trimming: a row missing
 * any of them cannot form the EZCap composite key, and sending a blank one is an
 * HTTP 400 (or worse, a 200 with a blank-patient claim). Null therefore means
 * exactly "this member cannot be claimed on", which is what the entry gate
 * blocks on.
 */
export function ppClaimSeedFromRecord(
  record: PpClaimRecordLike | null | undefined,
): PpClaimMemberSeed | null {
  if (!record) return null;
  const membKeyId = (record.membKeyId ?? "").trim();
  const hpCode = (record.healthPlanCode ?? "").trim();
  const startDate = (record.startDate ?? "").trim();
  if (!membKeyId || !hpCode || !startDate) return null;
  return {
    membKeyId,
    hpCode,
    startDate,
    sex: (record.gender ?? "").trim() || undefined,
  };
}

/**
 * PP member row seed → the module's `CreateClaimMemberContext`.
 *
 * Takes the SEED, never loose fields: every value here is already known to have
 * come from one row (see `ppClaimSeedFromRecord`), so no call site can transpose
 * or mix them. `RmiEndDate` is left `undefined` — `GetMembersForSearch` returns
 * the span's end date but the GraphQL member-search selection does not carry it,
 * and PP-API's claim shell endpoint does not require it.
 */
export function toClaimMemberFromSeed(seed: PpClaimMemberSeed): CreateClaimMemberContext {
  return {
    RmiMembKeyId: seed.membKeyId,
    RmiHpCode: seed.hpCode,
    RmiStartDate: seed.startDate,
    RmiEndDate: undefined,
    RmiSex: seed.sex,
  };
}

/**
 * Derives the `memberId` string for CreateClaimNativeProps from the available
 * member context. For PP members this is the membId (RmiMembId); for Pathways
 * members it is memberPolicyId (mirrors toPatientObject's memberId field).
 *
 * BLOCKED ON SAM: the PP-API claims endpoint key is unverified — membId vs
 * membMpiNo vs memberPolicyId. This mirrors Create Auth's memberIdHint, which
 * PP resolves on its side.
 */
export function toClaimMemberId(
  isPp: boolean,
  ppMemberId: string,
  loadedMember: NonNullable<MemberByIdResult["PathwaysMember"]> | null,
): string {
  if (isPp) return ppMemberId;
  // memberPolicyId mirrors toPatientObject's memberId mapping (EZCap contract)
  return loadedMember?.memberPolicyId ?? "";
}
